import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const PricingPage = () => {
  const [annual, setAnnual] = useState(true);

  const plans = [
    {
      name: "Starter",
      monthlyPrice: 29,
      annualPrice: 19,
      description: "Perfect for individuals and small projects",
      features: [
        "5 hours of processing/month",
        "Basic noise reduction",
        "Standard export formats",
        "Email support",
        "Up to 10 files per batch"
      ],
    },
    {
      name: "Professional",
      monthlyPrice: 69,
      annualPrice: 49,
      description: "For serious creators and small teams",
      features: [
        "Unlimited processing",
        "Advanced AI noise reduction",
        "All export formats",
        "Priority support",
        "API access",
        "Custom workflows",
        "Up to 100 files per batch"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      monthlyPrice: 199,
      annualPrice: 149,
      description: "For studios and large organizations",
      features: [
        "Everything in Professional",
        "Dedicated account manager",
        "Custom integrations",
        "99.9% SLA guarantee",
        "Team management",
        "White-label options",
        "Unlimited batch processing"
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-dark-base pt-24">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-dark-base/95 backdrop-blur border-b border-neon-orange/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-white font-heading font-bold text-xl hover:text-neon-orange transition-colors">
            ← Back to Home
          </Link>
          <h1 className="text-neon-orange font-heading font-bold">Pricing Plans</h1>
          <div className="w-24"></div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Simple, Transparent <span className="text-neon-orange">Pricing</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg mb-8">
            Start with a 14-day free trial. No credit card required.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4">
            <span className={`text-sm transition-colors ${!annual ? 'text-white' : 'text-gray-500'}`}>
              Monthly
            </span>
            <motion.button 
              className="w-16 h-8 bg-dark-deeper rounded-full relative border border-neon-orange/20"
              onClick={() => setAnnual(!annual)}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div 
                className="w-6 h-6 bg-neon-orange rounded-full absolute top-1"
                animate={{ x: annual ? 34 : 4 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            </motion.button>
            <span className={`text-sm transition-colors ${annual ? 'text-white' : 'text-gray-500'}`}>
              Annual
            </span>
            <motion.span 
              className="text-neon-orange text-sm font-semibold bg-neon-orange/10 px-3 py-1 rounded-full"
              animate={{ scale: annual ? [1, 1.1, 1] : 1 }}
              transition={{ duration: 0.3 }}
            >
              Save 30%
            </motion.span>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              className={`p-8 rounded-2xl border-2 transition-all ${
                plan.highlighted
                  ? 'border-neon-orange bg-neon-orange/10 scale-105 md:scale-110'
                  : 'border-neon-orange/20 bg-dark-deeper/50'
              }`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <h3 className="text-2xl font-heading font-bold text-white mb-2">{plan.name}</h3>
              <p className="text-gray-400 text-sm mb-6">{plan.description}</p>

              <div className="mb-6">
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold text-white">
                    ${annual ? plan.annualPrice : plan.monthlyPrice}
                  </span>
                  <span className="text-gray-500 ml-2">/month</span>
                </div>
                {annual && (
                  <p className="text-neon-orange text-sm mt-2">
                    Billed ${plan.annualPrice * 12}/year
                  </p>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, j) => (
                  <li key={j} className="flex items-start gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-neon-orange flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <motion.button
                className={`w-full py-3 rounded-full font-bold transition-all ${
                  plan.highlighted
                    ? 'bg-neon-orange text-white'
                    : 'border-2 border-neon-orange text-neon-orange hover:bg-neon-orange hover:text-white'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Get Started
              </motion.button>
            </motion.div>
          ))}
        </div>

        {/* FAQ Section */}
        <motion.div
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <h3 className="text-2xl font-heading font-bold text-white mb-8 text-center">Frequently Asked Questions</h3>
          <div className="space-y-4">
            {[
              { q: "Can I cancel anytime?", a: "Yes, cancel your subscription anytime with no penalties or hidden fees." },
              { q: "Is there a free trial?", a: "Yes, all plans include a 14-day free trial. No credit card required to start." },
              { q: "Can I upgrade or downgrade?", a: "Absolutely. Change your plan anytime and we'll adjust your billing accordingly." },
              { q: "Do you offer discounts for annual billing?", a: "Yes, save 30% when you pay annually instead of monthly." },
            ].map((item, i) => (
              <motion.div key={i} className="p-4 bg-dark-deeper rounded-lg border border-neon-orange/10">
                <h4 className="font-semibold text-white mb-2">{item.q}</h4>
                <p className="text-gray-400 text-sm">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingPage;
